/**
 * When the creature poops, and which poops have been picked up.
 *
 * Each day has its own little timetable of 3 to 5 poops, scattered between
 * 9am and 9:30pm. It is worked out from the date, so it is sporadic but stays
 * the same if the app is closed and reopened. It has nothing to do with what
 * or how much she ate, on purpose: logging food should never feel like it
 * makes a mess.
 *
 * Which ones have been picked up is remembered on the phone itself (not in the
 * database). Every day starts clean: anything left over from yesterday is
 * simply gone, because the creature never holds a grudge.
 *
 * ---------------------------------------------------------------------------
 * To change how often it goes, edit the three numbers below.
 */
const FIRST_MINUTE = 9 * 60; // 9:00am
const LAST_MINUTE = 21.5 * 60; // 9:30pm
const FEWEST = 3;
const MOST = 5;

/** Where each poop can land, side to side (%) and up from the ground (px). */
const SPOTS = [
  { x: 9, y: 14 },
  { x: 91, y: 8 },
  { x: 23, y: 0 },
  { x: 77, y: 18 },
  { x: 50, y: -8 },
];

export type Poop = {
  id: number;
  /** Minutes after midnight when it appears. */
  minute: number;
  x: number;
  y: number;
};

/** A repeatable stream of "random" numbers, 0 to 1, that depends on the date. */
function dice(date: string): () => number {
  let seed = 0;
  for (const character of date) seed = (seed * 31 + character.charCodeAt(0)) >>> 0;
  return () => {
    // The standard small "mulberry32" recipe for shuffled-looking numbers.
    seed = (seed + 0x6d2b79f5) >>> 0;
    let mixed = seed;
    mixed = Math.imul(mixed ^ (mixed >>> 15), mixed | 1);
    mixed ^= mixed + Math.imul(mixed ^ (mixed >>> 7), mixed | 61);
    return ((mixed ^ (mixed >>> 14)) >>> 0) / 4294967296;
  };
}

/** The whole timetable for a day. */
function timetable(date: string): Poop[] {
  const roll = dice(date);
  const count = FEWEST + Math.floor(roll() * (MOST - FEWEST + 1));
  // One slot of the day per poop, with each somewhere random inside its slot,
  // so they scatter without all landing in the same hour.
  const slot = (LAST_MINUTE - FIRST_MINUTE) / count;
  const spots = [...SPOTS].sort(() => roll() - 0.5);

  return Array.from({ length: count }, (_, id) => ({
    id,
    minute: FIRST_MINUTE + slot * (id + roll()),
    ...spots[id],
  }));
}

/** Every poop that has happened by now on this date, picked up or not. */
export function poopsSoFar(date: string, now: Date): Poop[] {
  const minute = now.getHours() * 60 + now.getMinutes();
  return timetable(date).filter((poop) => poop.minute <= minute);
}

const STORAGE_KEY = "poops-picked-up";

/**
 * What has been picked up. Only one day is ever kept, so this never grows.
 * The copy held here is what counts; saving it on the phone is a bonus, because
 * some private browsing modes refuse to store anything.
 */
let pickedUp: { date: string; ids: number[] } | null = null;

function load(date: string): number[] {
  if (pickedUp === null) {
    try {
      pickedUp = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "null");
    } catch {
      pickedUp = null;
    }
  }
  return pickedUp?.date === date ? pickedUp.ids : [];
}

/** The poops still sitting on the ground. */
export function poopsOnTheGround(date: string, now: Date): Poop[] {
  const gone = load(date);
  return poopsSoFar(date, now).filter((poop) => !gone.includes(poop.id));
}

export function pickUp(date: string, id: number): void {
  pickedUp = { date, ids: [...load(date), id] };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(pickedUp));
  } catch {
    // Nowhere to save it. It stays picked up until the app is closed.
  }
}
